import { useEffect, useState } from 'react';
import { callApi } from '../api';
import { Section, ErrorBox, LoadingSpinner, EmptyState } from '../components/Section';
import { UserChip } from '../components/UserChip';
import { useSession } from '../hooks/useSession';
import { timeAgo } from '../utils';

const STATUSES = ['open', 'closed', 'all'];

export function Feedback() {
  const { session } = useSession();
  const [status, setStatus] = useState('open');
  const [items, setItems] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [cursor, setCursor] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  const refresh = async (s = status, append = false, cur = null) => {
    if (!append) setLoading(true);
    try {
      const d = await callApi(
        `/api/v1/admin/feedback?status=${s}&limit=50${cur ? `&cursor=${encodeURIComponent(cur)}` : ''}`,
        { accessToken: session.access_token }
      );
      setItems((prev) => (append ? [...prev, ...d.items] : d.items));
      setHasMore(d.has_more);
      setCursor(d.next_cursor);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh(status);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, session.access_token]);

  const close = async (id) => {
    try {
      await callApi(`/api/v1/admin/feedback/${id}/close`, {
        method: 'POST',
        accessToken: session.access_token,
      });
      await refresh(status);
    } catch (e) {
      setError(e.message);
    }
  };

  const reopen = async (id) => {
    try {
      await callApi(`/api/v1/admin/feedback/${id}/reopen`, {
        method: 'POST',
        accessToken: session.access_token,
      });
      await refresh(status);
    } catch (e) {
      setError(e.message);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-stone-800">Feedback</h1>
        <div className="flex gap-1">
          {STATUSES.map((s) => (
            <button
              key={s}
              onClick={() => setStatus(s)}
              className={`text-xs px-3 py-1 rounded capitalize ${
                status === s
                  ? 'bg-stone-800 text-white'
                  : 'bg-white text-stone-700 border'
              }`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      <ErrorBox message={error} />
      {loading ? <LoadingSpinner /> : (
        items.length === 0 ? (
          <Section><EmptyState>No feedback in this view</EmptyState></Section>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {items.map((f) => (
              <FeedbackCard
                key={f.id}
                feedback={f}
                onClose={() => close(f.id)}
                onReopen={() => reopen(f.id)}
              />
            ))}
          </div>
        )
      )}

      {hasMore && (
        <button
          onClick={() => refresh(status, true, cursor)}
          className="text-sm text-stone-700 underline"
        >
          Load more
        </button>
      )}
    </div>
  );
}

function FeedbackCard({ feedback, onClose, onReopen }) {
  return (
    <div className="bg-white rounded-2xl shadow p-4 space-y-2">
      <div className="flex justify-between items-start">
        <div className="min-w-0">
          <UserChip
            userId={feedback.user_id}
            displayName={feedback.display_name}
            avatarUrl={feedback.avatar_url}
          />
          {feedback.phone && (
            <div className="text-xs text-stone-500 mt-0.5 ml-8">{feedback.phone}</div>
          )}
        </div>
        <span className="text-xs text-stone-500">{timeAgo(feedback.created_at)}</span>
      </div>
      {feedback.photos && feedback.photos.length > 0 && (
        <div className="grid grid-cols-2 gap-1.5">
          {feedback.photos.map((p) => (
            <a key={p.id} href={p.photo_url} target="_blank" rel="noreferrer">
              <img
                src={p.photo_url}
                alt=""
                className="w-full h-32 object-cover rounded bg-stone-100"
              />
            </a>
          ))}
        </div>
      )}
      <p className="text-sm text-stone-700 italic">{feedback.feedback_text}</p>
      <div className="flex justify-between items-center pt-1">
        <span className={`text-xs ${feedback.status === 'open' ? 'text-amber-700' : 'text-green-700'}`}>
          {feedback.status}
          {feedback.closed_at && ` · ${timeAgo(feedback.closed_at)}`}
        </span>
        {feedback.status === 'open' ? (
          <button
            onClick={onClose}
            className="text-xs bg-stone-800 text-white rounded px-3 py-1"
          >
            Mark closed
          </button>
        ) : (
          <button
            onClick={onReopen}
            className="text-xs text-stone-600 underline"
          >
            Reopen
          </button>
        )}
      </div>
    </div>
  );
}
